/**
 * Shared email validation (admin-frontend UX tier).
 *
 * MIRROR of backend/src/shared/validateEmail.ts — the rule must stay identical
 * in both tiers. The frontend copy exists because the admin app has no
 * build-time import path into the backend package; if you change the rule here,
 * change it there too (the backend jest battery guards the rule).
 *
 * DESIGN RULE — BE PERMISSIVE. A false negative (rejecting a REAL, valid email)
 * is a critical failure: a real client must never be blocked. We use the exact
 * WHATWG/HTML5 `<input type=email>` regex. Do NOT swap in a stricter pattern.
 *
 * The value is NEVER rewritten/normalised — validate the raw string as typed.
 */

export interface EmailValidationResult {
  ok: boolean
  error?: string
}

/** User-facing error, matching the ClientsView tone. */
export const EMAIL_ERROR = 'Enter a valid email address'

// The WHATWG/HTML5 `type=email` regex, verbatim.
export const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

/**
 * Validate an email string. Empty is INVALID here; callers treat email as
 * optional by skipping validation when the field is blank.
 */
export function validateEmail(raw: string): EmailValidationResult {
  const s = String(raw ?? '').trim()
  if (!s) return { ok: false, error: EMAIL_ERROR }
  if (!EMAIL_RE.test(s)) return { ok: false, error: EMAIL_ERROR }
  return { ok: true }
}

export default validateEmail
